/**
 * Chunk pump: fetches timeline chunks in playhead order with bounded concurrency, validates them
 * (length + header against the manifest, md5 optional), decodes them and hands transferable buffers to a
 * sink. Runs inside the module worker (worker.ts) and, as a fallback, on the main thread (node tests,
 * no Worker support). Pure apart from `fetch` and the retry timers.
 *
 * Failures: each chunk gets `backoffMs.length + 1` attempts (default 3: retry after 250 ms and 1 s). A chunk
 * that still fails is reported once as an 'error' message; the pump keeps going with the other chunks and
 * re-fetches a failed chunk on `retry(i)`. `stop()` aborts in-flight fetches and drops everything.
 */
import { decodeChunk, type DtypeCode } from './chunks.ts';
import { md5Hex } from './md5.ts';
import type { TimelineFile } from './manifest.gen.ts';

export const DEFAULT_BACKOFF_MS: readonly number[] = [250, 1000];

export interface PumpInit {
  type: 'init';
  baseUrl: string;
  files: TimelineFile[];
  /** [field id, exact f64 scale] */
  scales: [number, number][];
  nCars: number;
  /** chunk indices in fetch order (playhead first) */
  order: number[];
  verifyMd5: boolean;
  concurrency: number;
  /** waits before the 2nd, 3rd, … attempt (default DEFAULT_BACKOFF_MS) */
  backoffMs?: number[];
}
export interface PumpPrioritize { type: 'prioritize'; i: number }
export interface PumpRetry { type: 'retry'; i: number }
export interface PumpStop { type: 'stop' }
export type ToWorker = PumpInit | PumpPrioritize | PumpRetry | PumpStop;

export interface ChunkField { id: number; dtype: DtypeCode; buffer: ArrayBuffer }
export interface ChunkMsg { type: 'chunk'; i: number; f0: number; n: number; bytes: number; fields: ChunkField[] }
export interface ErrorMsg { type: 'error'; i: number; message: string }
export type FromWorker = ChunkMsg | ErrorMsg;

export type FetchFn = (url: string, init?: { signal?: AbortSignal }) => Promise<Response>;
export type Sink = (msg: FromWorker, transfer: ArrayBuffer[]) => void;

/**
 * Chunk fetch order for a playhead at chunk i0: i0, i0−1, i0+1, i0+2, …, last, then i0−2 down to 0
 * (the chunk before the playhead comes second: stateAt reads frame i−1 and short rewinds are common).
 */
export function playheadOrder(nChunks: number, i0: number): number[] {
  if (nChunks <= 0) return [];
  const s = Math.min(Math.max(Math.floor(i0), 0), nChunks - 1);
  const out: number[] = [s];
  if (s - 1 >= 0) out.push(s - 1);
  for (let i = s + 1; i < nChunks; i++) out.push(i);
  for (let i = s - 2; i >= 0; i--) out.push(i);
  return out;
}

export function joinUrl(base: string, rel: string): string {
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  const r = rel.startsWith('/') ? rel.slice(1) : rel;
  return `${b}/${r}`;
}

export class ChunkPump {
  private readonly queue: number[];
  /** fetched, in flight or failed (not re-queued until retry) */
  private readonly started = new Set<number>();
  private inFlight = 0;
  private stopped = false;
  private readonly scales: Map<number, number>;
  private readonly abort = new AbortController();
  private readonly backoff: readonly number[];

  constructor(private readonly init: PumpInit, private readonly fetchFn: FetchFn, private readonly sink: Sink) {
    this.queue = init.order.slice();
    this.scales = new Map(init.scales);
    this.backoff = init.backoffMs ?? DEFAULT_BACKOFF_MS;
  }

  start(): void {
    this.fill();
  }

  /** move chunk i to the front of the queue (seek to an unloaded region); no-op once started */
  prioritize(i: number): void {
    if (this.stopped || this.started.has(i)) return;
    const k = this.queue.indexOf(i);
    if (k > 0) {
      this.queue.splice(k, 1);
      this.queue.unshift(i);
    }
    this.fill();
  }

  /** re-fetch a chunk that was reported as failed (front of the queue) */
  retry(i: number): void {
    if (this.stopped || !this.started.has(i)) return;
    this.started.delete(i);
    const k = this.queue.indexOf(i);
    if (k >= 0) this.queue.splice(k, 1);
    this.queue.unshift(i);
    this.fill();
  }

  /** abort in-flight fetches; no further messages */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.queue.length = 0;
    this.abort.abort();
  }

  /** a method (not the field) so TS does not narrow across awaits */
  isStopped(): boolean {
    return this.stopped;
  }

  private fill(): void {
    const max = Math.max(1, this.init.concurrency);
    while (!this.stopped && this.inFlight < max && this.queue.length > 0) {
      const i = this.queue.shift();
      if (i === undefined || this.started.has(i)) continue;
      this.started.add(i);
      this.inFlight++;
      void this.load(i).finally(() => {
        this.inFlight--;
        this.fill();
      });
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      if (ms <= 0 || this.stopped) { resolve(); return; }
      const id = setTimeout(resolve, ms);
      this.abort.signal.addEventListener('abort', () => { clearTimeout(id); resolve(); }, { once: true });
    });
  }

  private async load(i: number): Promise<void> {
    const fe = this.init.files[i];
    if (!fe) {
      this.sink({ type: 'error', i, message: `no chunk ${String(i)} in the manifest` }, []);
      return;
    }
    let lastErr = '';
    for (let attempt = 0; attempt <= this.backoff.length; attempt++) {
      if (attempt > 0) await this.sleep(this.backoff[attempt - 1] ?? 0);
      if (this.stopped) return;
      try {
        const res = await this.fetchFn(joinUrl(this.init.baseUrl, fe.file), { signal: this.abort.signal });
        if (!res.ok) throw new Error(`HTTP ${String(res.status)} for ${fe.file}`);
        const buf = await res.arrayBuffer();
        if (this.isStopped()) return;                       // stop() may have run during the await
        if (buf.byteLength !== fe.bytes) throw new Error(`${fe.file}: ${String(buf.byteLength)} bytes, manifest says ${String(fe.bytes)}`);
        if (this.init.verifyMd5) {
          const h = md5Hex(buf);
          if (h !== fe.md5) throw new Error(`${fe.file}: md5 ${h} != manifest ${fe.md5}`);
        }
        const dec = decodeChunk(buf, this.scales, { f0: fe.f0, n: fe.n, nCars: this.init.nCars, bytes: fe.bytes });
        const fields: ChunkField[] = [];
        const transfer: ArrayBuffer[] = [];
        for (const e of dec.table) {
          const a = dec.fields.get(e.id);
          if (!a) continue;
          const b = a.buffer as ArrayBuffer;   // freshly allocated per field, never shared
          fields.push({ id: e.id, dtype: e.dtype, buffer: b });
          transfer.push(b);
        }
        this.sink({ type: 'chunk', i, f0: fe.f0, n: fe.n, bytes: fe.bytes, fields }, transfer);
        return;
      } catch (err) {
        if (this.isStopped()) return;
        lastErr = err instanceof Error ? err.message : String(err);
      }
    }
    this.sink({ type: 'error', i, message: lastErr }, []);
  }
}
